/**
 * Portfolios — Executive Portfolio Module (Board of Directors oversight).
 *
 * List all portfolios in the workspace and let admins create new ones.
 * Clicking a portfolio opens the executive dashboard (`PortfolioDetail`).
 */
import { useEffect, useState } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import api from "@/lib/api";
import { useAuth } from "@/lib/auth";
import PageHeader from "@/components/PageHeader";
import EmptyState from "@/components/EmptyState";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Label } from "@/components/ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle,
} from "@/components/ui/dialog";
import { PieChart, Plus, FolderKanban, Users, ArrowUpRight } from "lucide-react";
import { toast } from "sonner";
import { apiErrorMessage } from "@/lib/apiError";
import { fmtRelative } from "@/lib/format";

export default function Portfolios() {
  const { activeWorkspaceId } = useAuth();
  const nav = useNavigate();
  const [searchParams, setSearchParams] = useSearchParams();
  const [portfolios, setPortfolios] = useState([]);
  const [projects, setProjects] = useState([]);
  const [clients, setClients] = useState([]);
  const [open, setOpen] = useState(searchParams.get("new") === "1");
  const [form, setForm] = useState({
    name: "", description: "",
    project_ids: [], client_ids: [],
    digest_recipients: "",
  });
  const [busy, setBusy] = useState(false);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    if (!activeWorkspaceId) return;
    setLoading(true);
    try {
      const [pfs, ps, cs] = await Promise.all([
        api.get(`/workspaces/${activeWorkspaceId}/portfolios`),
        api.get(`/workspaces/${activeWorkspaceId}/projects`),
        api.get(`/workspaces/${activeWorkspaceId}/clients`),
      ]);
      setPortfolios(pfs.data || []);
      setProjects(ps.data || []);
      setClients(cs.data || []);
    } catch (e) {
      toast.error(apiErrorMessage(e, "Failed to load portfolios"));
    } finally { setLoading(false); }
  };

  useEffect(() => { load(); }, [activeWorkspaceId]); // eslint-disable-line

  useEffect(() => {
    if (searchParams.get("new") === "1") {
      const next = new URLSearchParams(searchParams);
      next.delete("new");
      setSearchParams(next, { replace: true });
    }
  }, []); // eslint-disable-line

  const submit = async (e) => {
    e.preventDefault();
    if (!form.name.trim()) { toast.error("Name required"); return; }
    setBusy(true);
    try {
      const payload = {
        name: form.name.trim(),
        description: form.description || undefined,
        project_ids: form.project_ids,
        client_ids: form.client_ids,
        digest_recipients: form.digest_recipients
          ? form.digest_recipients.split(",").map((r) => r.trim()).filter(Boolean)
          : [],
        digest_enabled: false,
      };
      const { data } = await api.post(
        `/workspaces/${activeWorkspaceId}/portfolios`, payload);
      toast.success("Portfolio created");
      setOpen(false);
      setForm({ name: "", description: "", project_ids: [],
                client_ids: [], digest_recipients: "" });
      nav(`/app/portfolios/${data.id}`);
    } catch (err) {
      toast.error(apiErrorMessage(err, "Failed to create portfolio"));
    } finally { setBusy(false); }
  };

  const toggleId = (kind, id) => {
    const key = kind === "project" ? "project_ids" : "client_ids";
    const list = form[key];
    const next = list.includes(id) ? list.filter((x) => x !== id) : [...list, id];
    setForm({ ...form, [key]: next });
  };

  return (
    <>
      <PageHeader
        eyebrow="Board of Directors"
        title="Executive Portfolios"
        description="Roll up multiple clients or projects into a single boardroom view — health, budgets, milestones, and AI-drafted headlines."
        testid="portfolios-header"
        actions={activeWorkspaceId && (
          <Button
            className="rounded-none"
            onClick={() => setOpen(true)}
            data-testid="portfolios-new-btn"
          >
            <Plus className="w-3.5 h-3.5 mr-1.5" /> New portfolio
          </Button>
        )}
      />

      <div className="px-4 sm:px-6 md:px-10 py-6">
        {loading && portfolios.length === 0 ? (
          <div className="text-sm text-muted-foreground">Loading portfolios…</div>
        ) : portfolios.length === 0 ? (
          <EmptyState
            title="No portfolios yet"
            description="Group your top strategic projects under one portfolio to give leadership a single pane of glass."
            icon={PieChart}
            testid="portfolios-empty"
            action={<Button onClick={() => setOpen(true)} className="rounded-none">Create your first portfolio</Button>}
          />
        ) : (
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4" data-testid="portfolios-grid">
            {portfolios.map((p) => (
              <button
                key={p.id}
                onClick={() => nav(`/app/portfolios/${p.id}`)}
                className="text-left border border-border p-6 hover:border-primary/40 transition group"
                data-testid={`portfolio-card-${p.id}`}
              >
                <div className="flex items-start justify-between mb-3">
                  <PieChart className="w-5 h-5 text-primary" />
                  <ArrowUpRight className="w-4 h-4 text-muted-foreground group-hover:text-primary" />
                </div>
                <h3 className="font-bold text-lg mb-1"
                    style={{ fontFamily: "var(--font-heading)" }}
                    data-testid={`portfolio-name-${p.id}`}>
                  {p.name}
                </h3>
                {p.description && (
                  <p className="text-sm text-muted-foreground line-clamp-2 mb-3">
                    {p.description}
                  </p>
                )}
                <div className="flex items-center gap-3 text-xs text-muted-foreground">
                  <span className="flex items-center gap-1">
                    <FolderKanban className="w-3 h-3" /> {p.project_count || 0} projects
                  </span>
                  {p.client_ids?.length > 0 && (
                    <span className="flex items-center gap-1">
                      <Users className="w-3 h-3" /> {p.client_ids.length} clients
                    </span>
                  )}
                  {p.digest_enabled && (
                    <span className="text-emerald-600">Weekly digest ON</span>
                  )}
                </div>
                {p.last_digest_at && (
                  <div className="text-[10px] text-muted-foreground mt-2">
                    Last digest {fmtRelative(p.last_digest_at)}
                  </div>
                )}
              </button>
            ))}
          </div>
        )}
      </div>

      <Dialog open={open} onOpenChange={setOpen}>
        <DialogContent className="max-w-2xl" data-testid="portfolio-new-dialog">
          <DialogHeader>
            <DialogTitle>Create new portfolio</DialogTitle>
          </DialogHeader>
          <form onSubmit={submit} className="space-y-3">
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Name *</Label>
              <Input required value={form.name}
                     onChange={(e) => setForm({ ...form, name: e.target.value })}
                     placeholder="e.g. Growth Portfolio Q2"
                     data-testid="portfolio-form-name" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">Description</Label>
              <Textarea rows={2} value={form.description}
                        onChange={(e) => setForm({ ...form, description: e.target.value })}
                        data-testid="portfolio-form-desc" />
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">
                Clients (all their projects roll up)
              </Label>
              <div className="max-h-32 overflow-y-auto border border-border p-2 space-y-1">
                {clients.length === 0 && (
                  <div className="text-xs text-muted-foreground">No clients yet.</div>
                )}
                {clients.map((c) => (
                  <label key={c.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox"
                           checked={form.client_ids.includes(c.id)}
                           onChange={() => toggleId("client", c.id)}
                           data-testid={`portfolio-form-client-${c.id}`} />
                    <span>{c.name}</span>
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">
                Specific projects (optional)
              </Label>
              <div className="max-h-32 overflow-y-auto border border-border p-2 space-y-1">
                {projects.length === 0 && (
                  <div className="text-xs text-muted-foreground">No projects yet.</div>
                )}
                {projects.map((p) => (
                  <label key={p.id} className="flex items-center gap-2 text-sm cursor-pointer">
                    <input type="checkbox"
                           checked={form.project_ids.includes(p.id)}
                           onChange={() => toggleId("project", p.id)}
                           data-testid={`portfolio-form-project-${p.id}`} />
                    <span>{p.name}</span>
                    {p.client_name && (
                      <span className="text-xs text-muted-foreground">· {p.client_name}</span>
                    )}
                  </label>
                ))}
              </div>
            </div>
            <div>
              <Label className="label-eyebrow mb-1 inline-block">
                Weekly digest recipients (comma-separated emails)
              </Label>
              <Input value={form.digest_recipients}
                     onChange={(e) => setForm({ ...form, digest_recipients: e.target.value })}
                     placeholder="board@company.com, ceo@company.com"
                     data-testid="portfolio-form-recipients" />
            </div>
            <Button type="submit" disabled={busy} className="w-full rounded-none"
                    data-testid="portfolio-form-submit">
              {busy ? "Creating…" : "Create portfolio"}
            </Button>
          </form>
        </DialogContent>
      </Dialog>
    </>
  );
}
